import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createPlatformTenant,
  getPlatformTenant,
  mapLaravelError,
  updatePlatformTenant,
} from '../../services/platformTenantsApi';

const TENANT_BASE_PATH = '/super-admin/platform/tenants';

// Carbon dayOfWeek numbering (0=Sun … 6=Sat) — matches the backend weekend config.
const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const emptyFeatureFlag = () => ({
  feature_key: '',
  is_enabled: true,
  feature_value_json: '',
});

// Per-tenant automated approval-reminder feature. Persisted as a single feature
// flag with feature_key === REMINDER_FLAG_KEY; the settings object below is the
// flag's feature_value, and the on/off toggle is the flag's is_enabled.
const REMINDER_FLAG_KEY = 'request_reminders';

const REMINDER_DOMAIN_OPTIONS = [
  { value: 'hr_request', label: 'HR requests (leave, loans, finance, pre-approval, etc.)' },
  { value: 'vacation', label: 'Vacation requests' },
  { value: 'exit_entry', label: 'Exit/Entry requests' },
];

const REMINDER_DEFAULTS = {
  threshold_days: 3,
  cadence_days: 3,
  max_reminders: 5,
  domains: REMINDER_DOMAIN_OPTIONS.map((option) => option.value),
  skip_weekends: true,
};

// Read the request_reminders flag out of form state, parsing its feature_value_json
// and falling back to the defaults above for any missing/invalid pieces.
const getReminderSettings = (form) => {
  const flag = (form?.feature_flags || []).find((entry) => entry.feature_key === REMINDER_FLAG_KEY);

  let parsed = {};
  if (flag) {
    const raw = String(flag.feature_value_json || '').trim();
    if (raw) {
      try {
        const value = JSON.parse(raw);
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          parsed = value;
        }
      } catch {
        parsed = {};
      }
    }
  }

  const allowedDomains = REMINDER_DOMAIN_OPTIONS.map((option) => option.value);
  const domains = Array.isArray(parsed.domains)
    ? parsed.domains.filter((domain) => allowedDomains.includes(domain))
    : REMINDER_DEFAULTS.domains;

  return {
    is_enabled: flag ? Boolean(flag.is_enabled) : false,
    threshold_days: Number.isFinite(Number(parsed.threshold_days))
      ? Number(parsed.threshold_days)
      : REMINDER_DEFAULTS.threshold_days,
    cadence_days: Number.isFinite(Number(parsed.cadence_days))
      ? Number(parsed.cadence_days)
      : REMINDER_DEFAULTS.cadence_days,
    max_reminders: Number.isFinite(Number(parsed.max_reminders))
      ? Number(parsed.max_reminders)
      : REMINDER_DEFAULTS.max_reminders,
    domains: domains.length ? domains : REMINDER_DEFAULTS.domains,
    skip_weekends: typeof parsed.skip_weekends === 'boolean'
      ? parsed.skip_weekends
      : REMINDER_DEFAULTS.skip_weekends,
  };
};

const emptyHrAdmin = () => ({
  name: '',
  employee_code: '',
  email: '',
  password: '',
  gender: '',
});

const createInitialForm = () => ({
  key: '',
  org_code: '',
  display_name: '',
  short_name: '',
  product_label: '',
  status: 'active',
  auth_mode: 'password',
  weekend_days: [],
  domains_text: '',
  branding: {
    display_name: '',
    short_name: '',
    primary_color: '#1f3d7c',
    secondary_color: '#2389ba',
    accent_color: '#75913b',
    email_from_name: '',
  },
  database_connection: {
    driver: 'mysql',
    host: '127.0.0.1',
    port: '3306',
    database: '',
    username: '',
    password: '',
    unix_socket: '',
    charset: 'utf8mb4',
    collation: 'utf8mb4_unicode_ci',
  },
  provisioning: {
    create_database: true,
    run_migrations: true,
  },
  feature_flags: [],
  branding_logo_light: null,
  branding_logo_dark: null,
  branding_favicon: null,
  branding_pdf_header_logo: null,
  hr_admin: emptyHrAdmin(),
  existing_hr_admin: null,
});

const parseDomains = (value) => String(value || '')
  .split(/\r?\n/)
  .map((item) => item.trim())
  .filter(Boolean);

const getFieldError = (fieldErrors, key) => fieldErrors?.[key] || '';

const normalizeColorForPicker = (value, fallback = '#1f3d7c') => {
  const text = String(value || '').trim();
  const hex = text.startsWith('#') ? text : `#${text}`;
  return /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : fallback;
};

const normalizeTenantPayloadToForm = (payload = {}) => {
  const tenant = payload?.tenant || {};
  const brandingRecord = tenant?.branding || {};
  const db = tenant?.database_connection || {};
  const featureFlags = Array.isArray(tenant?.feature_flags) ? tenant.feature_flags : [];
  const domains = Array.isArray(tenant?.domains) ? tenant.domains.map((item) => item.domain).filter(Boolean) : [];
  const hrAdmin = payload?.hr_admin || null;

  return {
    key: tenant.key || '',
    org_code: tenant.org_code || '',
    display_name: tenant.display_name || '',
    short_name: tenant.short_name || '',
    product_label: tenant.product_label || '',
    status: tenant.status || 'active',
    auth_mode: tenant.auth_mode || 'password',
    weekend_days: Array.isArray(tenant.weekend_days) ? tenant.weekend_days.map(Number) : [],
    domains_text: domains.join('\n'),
    branding: {
      display_name: brandingRecord.display_name || '',
      short_name: brandingRecord.short_name || '',
      primary_color: brandingRecord.primary_color || '#1f3d7c',
      secondary_color: brandingRecord.secondary_color || '#2389ba',
      accent_color: brandingRecord.accent_color || '#75913b',
      email_from_name: brandingRecord.email_from_name || '',
    },
    database_connection: {
      driver: db.driver || 'mysql',
      host: db.host || '127.0.0.1',
      port: db.port || '3306',
      database: db.database || '',
      username: db.username || '',
      password: '',
      unix_socket: db.unix_socket || '',
      charset: db.charset || 'utf8mb4',
      collation: db.collation || 'utf8mb4_unicode_ci',
    },
    provisioning: {
      create_database: false,
      run_migrations: false,
    },
    feature_flags: featureFlags.map((flag) => ({
      feature_key: flag.feature_key || '',
      is_enabled: Boolean(flag.is_enabled),
      feature_value_json: flag.feature_value ? JSON.stringify(flag.feature_value, null, 2) : '',
    })),
    branding_logo_light: null,
    branding_logo_dark: null,
    branding_favicon: null,
    branding_pdf_header_logo: null,
    hr_admin: emptyHrAdmin(),
    existing_hr_admin: hrAdmin,
  };
};

const buildSubmitPayload = (form) => {
  const payload = {
    key: form.key,
    org_code: form.org_code,
    display_name: form.display_name,
    short_name: form.short_name || null,
    product_label: form.product_label || null,
    status: form.status,
    auth_mode: form.auth_mode,
    weekend_days: Array.isArray(form.weekend_days) ? form.weekend_days.map(Number) : [],
    domains: parseDomains(form.domains_text),
    branding: {
      display_name: form.branding.display_name || null,
      short_name: form.branding.short_name || null,
      primary_color: form.branding.primary_color || null,
      secondary_color: form.branding.secondary_color || null,
      accent_color: form.branding.accent_color || null,
      email_from_name: form.branding.email_from_name || null,
    },
    database_connection: {
      driver: form.database_connection.driver || null,
      host: form.database_connection.host || null,
      port: form.database_connection.port || null,
      database: form.database_connection.database,
      username: form.database_connection.username || null,
      password: form.database_connection.password || null,
      unix_socket: form.database_connection.unix_socket || null,
      charset: form.database_connection.charset || null,
      collation: form.database_connection.collation || null,
    },
    provisioning: {
      create_database: Boolean(form.provisioning.create_database),
      run_migrations: Boolean(form.provisioning.run_migrations),
    },
    feature_flags: form.feature_flags
      .filter((flag) => String(flag.feature_key || '').trim() !== '')
      .map((flag, index) => {
        const raw = String(flag.feature_value_json || '').trim();
        try {
          return {
            feature_key: flag.feature_key,
            is_enabled: Boolean(flag.is_enabled),
            feature_value: raw ? JSON.parse(raw) : null,
          };
        } catch {
          const error = new Error(`Invalid JSON for feature flag ${index + 1}.`);
          error.fieldKey = `feature_flags.${index}.feature_value`;
          throw error;
        }
      }),
  };

  if (!form.existing_hr_admin) {
    const hrAdmin = {
      name: String(form.hr_admin.name || '').trim(),
      employee_code: String(form.hr_admin.employee_code || '').trim(),
      email: String(form.hr_admin.email || '').trim(),
      password: String(form.hr_admin.password || ''),
      gender: String(form.hr_admin.gender || '').trim(),
    };

    const hasHrAdminInput = Object.values(hrAdmin).some((value) => String(value || '').trim() !== '');
    if (hasHrAdminInput) {
      payload.hr_admin = hrAdmin;
    }
  }

  if (form.branding_logo_light instanceof File) {
    payload.branding_logo_light = form.branding_logo_light;
  }
  if (form.branding_logo_dark instanceof File) {
    payload.branding_logo_dark = form.branding_logo_dark;
  }
  if (form.branding_favicon instanceof File) {
    payload.branding_favicon = form.branding_favicon;
  }
  if (form.branding_pdf_header_logo instanceof File) {
    payload.branding_pdf_header_logo = form.branding_pdf_header_logo;
  }

  return payload;
};

const FileField = ({ label, helperText, onChange, selectedFile, accept }) => (
  <Stack spacing={0.75}>
    <Button variant="outlined" component="label">
      {label}
      <input
        hidden
        type="file"
        accept={accept}
        onChange={(event) => onChange(event.target.files?.[0] || null)}
      />
    </Button>
    <Typography variant="caption" color="text.secondary">
      {selectedFile ? selectedFile.name : helperText}
    </Typography>
  </Stack>
);

const ColorField = ({ label, value, onChange, error, helperText, fallback }) => {
  const pickerValue = normalizeColorForPicker(value, fallback);

  return (
    <Stack spacing={0.75}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
        <TextField
          fullWidth
          label={label}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          error={Boolean(error)}
          helperText={helperText || 'Use a 6-digit hex color.'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: 1,
                    bgcolor: pickerValue,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          type="color"
          value={pickerValue}
          onChange={(event) => onChange(event.target.value)}
          inputProps={{ 'aria-label': `${label} picker` }}
          sx={{
            width: { xs: '100%', sm: 76 },
            flexShrink: 0,
            '& input': {
              minHeight: 38,
              p: 0.75,
              cursor: 'pointer',
            },
          }}
        />
      </Stack>
    </Stack>
  );
};

const TenantStylePreview = ({ form }) => {
  const primary = normalizeColorForPicker(form.branding.primary_color, '#1f3d7c');
  const secondary = normalizeColorForPicker(form.branding.secondary_color, '#2389ba');
  const accent = normalizeColorForPicker(form.branding.accent_color, '#75913b');
  const title = form.branding.display_name || form.display_name || 'Tenant workspace';
  const productLabel = form.product_label || title;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: alpha(primary, 0.22),
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.4,
          color: '#ffffff',
          background: `linear-gradient(90deg, ${primary} 0%, ${secondary} 72%, ${accent} 100%)`,
        }}
      >
        <Typography variant="subtitle2" sx={{ color: 'inherit' }}>
          Interface preview
        </Typography>
        <Typography variant="caption" sx={{ color: alpha('#ffffff', 0.78) }}>
          Sidebar, top navigation, focus color, and badges use these tenant colors after login.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '180px 1fr' }, minHeight: 160 }}>
        <Box
          sx={{
            p: 1.25,
            color: '#ffffff',
            background: `linear-gradient(180deg, ${primary} 0%, ${alpha(primary, 0.98)} 52%, ${secondary} 100%)`,
          }}
        >
          <Typography variant="caption" sx={{ color: alpha('#ffffff', 0.72), display: 'block', mb: 1 }}>
            {title}
          </Typography>
          {['Dashboard', 'Requests', 'Profile'].map((item, index) => (
            <Box
              key={item}
              sx={{
                px: 1,
                py: 0.8,
                mb: 0.6,
                borderRadius: 1.4,
                color: index === 0 ? primary : alpha('#ffffff', 0.9),
                bgcolor: index === 0 ? alpha('#ffffff', 0.92) : alpha('#ffffff', 0.08),
                borderLeft: `3px solid ${index === 0 ? accent : 'transparent'}`,
                fontSize: '0.78rem',
                fontWeight: 800,
              }}
            >
              {item}
            </Box>
          ))}
        </Box>

        <Box sx={{ p: 1.5 }}>
          <Stack spacing={1.25}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  {productLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  Tenant-specific workspace styling
                </Typography>
              </Box>
              <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: primary, border: `2px solid ${accent}` }} />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[primary, secondary, accent].map((color) => (
                <Box
                  key={color}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1,
                    py: 0.6,
                    borderRadius: 999,
                    bgcolor: alpha(color, 0.1),
                    color,
                    fontSize: '0.76rem',
                    fontWeight: 800,
                  }}
                >
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
                  {color}
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                height: 34,
                borderRadius: 1.4,
                background: `linear-gradient(90deg, ${alpha(accent, 0.18)}, ${alpha(secondary, 0.16)})`,
                border: `1px solid ${alpha(secondary, 0.22)}`,
              }}
            />
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default function TenantFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const tenantId = Number(id || 0);
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState(createInitialForm);

  const pageTitle = useMemo(() => (isEditMode ? 'Edit Tenant' : 'Create Tenant'), [isEditMode]);

  const loadTenant = useCallback(async () => {
    if (!isEditMode) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const payload = await getPlatformTenant(tenantId);
      setForm(normalizeTenantPayloadToForm(payload));
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setLoading(false);
    }
  }, [isEditMode, tenantId]);

  useEffect(() => {
    loadTenant();
  }, [loadTenant]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateBrandingField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      branding: {
        ...prev.branding,
        [key]: value,
      },
    }));
  };

  const updateDatabaseField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      database_connection: {
        ...prev.database_connection,
        [key]: value,
      },
    }));
  };

  const updateProvisioningField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      provisioning: {
        ...prev.provisioning,
        [key]: value,
      },
    }));
  };

  const updateHrAdminField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      hr_admin: {
        ...prev.hr_admin,
        [key]: value,
      },
    }));
  };

  const updateFeatureFlag = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      feature_flags: prev.feature_flags.map((flag, currentIndex) => (
        currentIndex === index ? { ...flag, [key]: value } : flag
      )),
    }));
  };

  const addFeatureFlag = () => {
    setForm((prev) => ({
      ...prev,
      feature_flags: [...prev.feature_flags, emptyFeatureFlag()],
    }));
  };

  const removeFeatureFlag = (index) => {
    setForm((prev) => ({
      ...prev,
      feature_flags: prev.feature_flags.filter((_, currentIndex) => currentIndex !== index),
    }));
  };

  // Upsert the request_reminders flag, merging `partial` over current settings and
  // re-stringifying into feature_value_json so it round-trips with buildSubmitPayload.
  const updateReminderSettings = (partial) => {
    setForm((prev) => {
      const current = getReminderSettings(prev);
      const next = { ...current, ...partial };
      const { is_enabled: nextEnabled, ...featureValue } = next;
      const featureValueJson = JSON.stringify(featureValue, null, 2);

      const existingIndex = prev.feature_flags.findIndex(
        (flag) => flag.feature_key === REMINDER_FLAG_KEY
      );

      const reminderFlag = {
        feature_key: REMINDER_FLAG_KEY,
        is_enabled: Boolean(nextEnabled),
        feature_value_json: featureValueJson,
      };

      const featureFlags = existingIndex >= 0
        ? prev.feature_flags.map((flag, index) => (index === existingIndex ? reminderFlag : flag))
        : [...prev.feature_flags, reminderFlag];

      return { ...prev, feature_flags: featureFlags };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setFieldErrors({});

    try {
      const payload = buildSubmitPayload(form);
      const response = isEditMode
        ? await updatePlatformTenant(tenantId, payload)
        : await createPlatformTenant(payload);

      navigate(`${TENANT_BASE_PATH}/${response?.id}`);
    } catch (requestError) {
      if (requestError?.fieldKey) {
        setError(requestError.message);
        setFieldErrors({ [requestError.fieldKey]: requestError.message });
      } else {
        const mapped = mapLaravelError(requestError);
        setError(mapped.message);
        setFieldErrors(mapped.fieldErrors);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack spacing={0.5}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {pageTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure tenant routing, identity, and isolated database connection details.
            </Typography>
          </Stack>

          <Button startIcon={<ArrowBackOutlinedIcon />} onClick={() => navigate(isEditMode ? `${TENANT_BASE_PATH}/${tenantId}` : TENANT_BASE_PATH)}>
            Back
          </Button>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Tenant Basics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Display Name"
                      value={form.display_name}
                      onChange={(event) => updateField('display_name', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'display_name'))}
                      helperText={getFieldError(fieldErrors, 'display_name')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Short Name"
                      value={form.short_name}
                      onChange={(event) => updateField('short_name', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'short_name'))}
                      helperText={getFieldError(fieldErrors, 'short_name')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      label="Tenant Key"
                      value={form.key}
                      onChange={(event) => updateField('key', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'key'))}
                      helperText={getFieldError(fieldErrors, 'key') || 'Used for tenant routing and queue scoping.'}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      label="Organization Code"
                      value={form.org_code}
                      onChange={(event) => updateField('org_code', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'org_code'))}
                      helperText={getFieldError(fieldErrors, 'org_code') || 'Used by mobile login and pre-auth bootstrap.'}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Product Label"
                      value={form.product_label}
                      onChange={(event) => updateField('product_label', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'product_label'))}
                      helperText={getFieldError(fieldErrors, 'product_label')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Status"
                      value={form.status}
                      onChange={(event) => updateField('status', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'status'))}
                      helperText={getFieldError(fieldErrors, 'status')}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="suspended">Suspended</MenuItem>
                      <MenuItem value="archived">Archived</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Auth Mode"
                      value={form.auth_mode}
                      onChange={(event) => updateField('auth_mode', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'auth_mode'))}
                      helperText={getFieldError(fieldErrors, 'auth_mode')}
                    >
                      <MenuItem value="password">Password</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Weekend Days"
                      value={form.weekend_days}
                      onChange={(event) =>
                        updateField(
                          'weekend_days',
                          (Array.isArray(event.target.value) ? event.target.value : []).map(Number)
                        )
                      }
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{
                        multiple: true,
                        renderValue: (selected) =>
                          selected && selected.length
                            ? selected
                                .map((v) => WEEKDAY_OPTIONS.find((o) => o.value === Number(v))?.label)
                                .filter(Boolean)
                                .join(', ')
                            : 'Default (Friday, Saturday)',
                      }}
                      error={Boolean(getFieldError(fieldErrors, 'weekend_days'))}
                      helperText={
                        getFieldError(fieldErrors, 'weekend_days') ||
                        'Days counted as weekend on timesheets. Leave empty for the default (Friday & Saturday).'
                      }
                    >
                      {WEEKDAY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Domains
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="Domains"
                  value={form.domains_text}
                  onChange={(event) => updateField('domains_text', event.target.value)}
                  helperText={getFieldError(fieldErrors, 'domains') || 'One domain per line. The first domain becomes primary.'}
                  error={Boolean(getFieldError(fieldErrors, 'domains'))}
                />
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Branding
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Brand Display Name"
                      value={form.branding.display_name}
                      onChange={(event) => updateBrandingField('display_name', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'branding.display_name'))}
                      helperText={getFieldError(fieldErrors, 'branding.display_name')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Brand Short Name"
                      value={form.branding.short_name}
                      onChange={(event) => updateBrandingField('short_name', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'branding.short_name'))}
                      helperText={getFieldError(fieldErrors, 'branding.short_name')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <ColorField
                      label="Primary Color"
                      value={form.branding.primary_color}
                      onChange={(value) => updateBrandingField('primary_color', value)}
                      error={getFieldError(fieldErrors, 'branding.primary_color')}
                      helperText={getFieldError(fieldErrors, 'branding.primary_color') || 'Used for top navigation and primary actions.'}
                      fallback="#1f3d7c"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <ColorField
                      label="Secondary Color"
                      value={form.branding.secondary_color}
                      onChange={(value) => updateBrandingField('secondary_color', value)}
                      error={getFieldError(fieldErrors, 'branding.secondary_color')}
                      helperText={getFieldError(fieldErrors, 'branding.secondary_color') || 'Used for hover states and navigation depth.'}
                      fallback="#2389ba"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <ColorField
                      label="Accent Color"
                      value={form.branding.accent_color}
                      onChange={(value) => updateBrandingField('accent_color', value)}
                      error={getFieldError(fieldErrors, 'branding.accent_color')}
                      helperText={getFieldError(fieldErrors, 'branding.accent_color') || 'Used for active states, borders, and highlights.'}
                      fallback="#75913b"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email From Name"
                      value={form.branding.email_from_name}
                      onChange={(event) => updateBrandingField('email_from_name', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'branding.email_from_name'))}
                      helperText={getFieldError(fieldErrors, 'branding.email_from_name')}
                    />
                  </Grid>
                </Grid>

                <TenantStylePreview form={form} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <FileField
                      label="Logo Light"
                      helperText="PNG/JPG up to 4MB"
                      selectedFile={form.branding_logo_light}
                      accept="image/*"
                      onChange={(file) => updateField('branding_logo_light', file)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FileField
                      label="Logo Dark"
                      helperText="PNG/JPG up to 4MB"
                      selectedFile={form.branding_logo_dark}
                      accept="image/*"
                      onChange={(file) => updateField('branding_logo_dark', file)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FileField
                      label="Favicon"
                      helperText="PNG/ICO/SVG up to 2MB"
                      selectedFile={form.branding_favicon}
                      accept=".png,.ico,.svg,image/png,image/svg+xml"
                      onChange={(file) => updateField('branding_favicon', file)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FileField
                      label="PDF Header Logo"
                      helperText="PNG/JPG up to 4MB"
                      selectedFile={form.branding_pdf_header_logo}
                      accept="image/*"
                      onChange={(file) => updateField('branding_pdf_header_logo', file)}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Database Connection
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Driver"
                      value={form.database_connection.driver}
                      onChange={(event) => updateDatabaseField('driver', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'database_connection.driver'))}
                      helperText={getFieldError(fieldErrors, 'database_connection.driver')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Host"
                      value={form.database_connection.host}
                      onChange={(event) => updateDatabaseField('host', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'database_connection.host'))}
                      helperText={getFieldError(fieldErrors, 'database_connection.host')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Port"
                      value={form.database_connection.port}
                      onChange={(event) => updateDatabaseField('port', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'database_connection.port'))}
                      helperText={getFieldError(fieldErrors, 'database_connection.port')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Database"
                      value={form.database_connection.database}
                      onChange={(event) => updateDatabaseField('database', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'database_connection.database'))}
                      helperText={getFieldError(fieldErrors, 'database_connection.database')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Username"
                      value={form.database_connection.username}
                      onChange={(event) => updateDatabaseField('username', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'database_connection.username'))}
                      helperText={getFieldError(fieldErrors, 'database_connection.username')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={isEditMode ? 'Password (leave blank to keep current)' : 'Password'}
                      type="password"
                      value={form.database_connection.password}
                      onChange={(event) => updateDatabaseField('password', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'database_connection.password'))}
                      helperText={getFieldError(fieldErrors, 'database_connection.password')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Unix Socket"
                      value={form.database_connection.unix_socket}
                      onChange={(event) => updateDatabaseField('unix_socket', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'database_connection.unix_socket'))}
                      helperText={getFieldError(fieldErrors, 'database_connection.unix_socket')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Charset"
                      value={form.database_connection.charset}
                      onChange={(event) => updateDatabaseField('charset', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'database_connection.charset'))}
                      helperText={getFieldError(fieldErrors, 'database_connection.charset')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Collation"
                      value={form.database_connection.collation}
                      onChange={(event) => updateDatabaseField('collation', event.target.value)}
                      error={Boolean(getFieldError(fieldErrors, 'database_connection.collation'))}
                      helperText={getFieldError(fieldErrors, 'database_connection.collation')}
                    />
                  </Grid>
                </Grid>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={Boolean(form.provisioning.create_database)}
                        onChange={(event) => updateProvisioningField('create_database', event.target.checked)}
                      />
                    )}
                    label="Create database if missing"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={Boolean(form.provisioning.run_migrations)}
                        onChange={(event) => updateProvisioningField('run_migrations', event.target.checked)}
                      />
                    )}
                    label="Run tenant migrations"
                  />
                </Stack>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Initial HR Admin
                </Typography>

                {form.existing_hr_admin ? (
                  <Alert severity="info">
                    This tenant already has an HR Admin account: {form.existing_hr_admin.name} ({form.existing_hr_admin.employee_code}).
                  </Alert>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Optional. If provided, the tenant will be provisioned with its first active HR Admin account.
                  </Typography>
                )}

                {form.existing_hr_admin ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth disabled label="Name" value={form.existing_hr_admin.name || ''} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth disabled label="Employee Code" value={form.existing_hr_admin.employee_code || ''} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth disabled label="Email" value={form.existing_hr_admin.email || ''} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth disabled label="Gender" value={form.existing_hr_admin.gender || '-'} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth disabled label="Status" value={form.existing_hr_admin.status || '-'} />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="HR Admin Name"
                        value={form.hr_admin.name}
                        onChange={(event) => updateHrAdminField('name', event.target.value)}
                        error={Boolean(getFieldError(fieldErrors, 'hr_admin.name'))}
                        helperText={getFieldError(fieldErrors, 'hr_admin.name')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="HR Admin Employee Code"
                        value={form.hr_admin.employee_code}
                        onChange={(event) => updateHrAdminField('employee_code', event.target.value)}
                        error={Boolean(getFieldError(fieldErrors, 'hr_admin.employee_code'))}
                        helperText={getFieldError(fieldErrors, 'hr_admin.employee_code')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="HR Admin Email"
                        value={form.hr_admin.email}
                        onChange={(event) => updateHrAdminField('email', event.target.value)}
                        error={Boolean(getFieldError(fieldErrors, 'hr_admin.email'))}
                        helperText={getFieldError(fieldErrors, 'hr_admin.email')}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        type="password"
                        label="HR Admin Password"
                        value={form.hr_admin.password}
                        onChange={(event) => updateHrAdminField('password', event.target.value)}
                        error={Boolean(getFieldError(fieldErrors, 'hr_admin.password'))}
                        helperText={getFieldError(fieldErrors, 'hr_admin.password') || 'Minimum 6 characters.'}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        select
                        label="HR Admin Gender"
                        value={form.hr_admin.gender}
                        onChange={(event) => updateHrAdminField('gender', event.target.value)}
                        error={Boolean(getFieldError(fieldErrors, 'hr_admin.gender'))}
                        helperText={getFieldError(fieldErrors, 'hr_admin.gender')}
                      >
                        <MenuItem value="">Not set</MenuItem>
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              {(() => {
                const reminders = getReminderSettings(form);
                const subFieldsDisabled = !reminders.is_enabled;
                return (
                  <Stack spacing={1.5}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Request reminders
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Automatically email approvers when a request has been waiting for their
                        action. Off unless enabled.
                      </Typography>
                    </Stack>

                    <FormControlLabel
                      control={(
                        <Switch
                          checked={reminders.is_enabled}
                          onChange={(event) => updateReminderSettings({ is_enabled: event.target.checked })}
                        />
                      )}
                      label="Enable automated approval reminders"
                    />

                    <Divider />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Threshold (days)"
                          value={reminders.threshold_days}
                          disabled={subFieldsDisabled}
                          onChange={(event) =>
                            updateReminderSettings({
                              threshold_days: Math.max(1, Math.trunc(Number(event.target.value) || 0)),
                            })
                          }
                          inputProps={{ min: 1, step: 1 }}
                          helperText="Send the first reminder after an item has waited this many days."
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Repeat every (days)"
                          value={reminders.cadence_days}
                          disabled={subFieldsDisabled}
                          onChange={(event) =>
                            updateReminderSettings({
                              cadence_days: Math.max(1, Math.trunc(Number(event.target.value) || 0)),
                            })
                          }
                          inputProps={{ min: 1, step: 1 }}
                          helperText="Re-remind every N days after the first reminder."
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Max reminders"
                          value={reminders.max_reminders}
                          disabled={subFieldsDisabled}
                          onChange={(event) =>
                            updateReminderSettings({
                              max_reminders: Math.max(0, Math.trunc(Number(event.target.value) || 0)),
                            })
                          }
                          inputProps={{ min: 0, step: 1 }}
                          helperText="Safety cap on repeats per item. 0 = unlimited."
                        />
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <TextField
                          fullWidth
                          select
                          label="Applies to"
                          value={reminders.domains}
                          disabled={subFieldsDisabled}
                          onChange={(event) => {
                            const selected = Array.isArray(event.target.value) ? event.target.value : [];
                            updateReminderSettings({ domains: selected });
                          }}
                          InputLabelProps={{ shrink: true }}
                          SelectProps={{
                            multiple: true,
                            renderValue: (selected) =>
                              selected && selected.length
                                ? selected
                                    .map((value) =>
                                      REMINDER_DOMAIN_OPTIONS.find((option) => option.value === value)?.label
                                    )
                                    .filter(Boolean)
                                    .join(', ')
                                : 'No request types selected',
                          }}
                          helperText="Which request types send approval reminders."
                        >
                          {REMINDER_DOMAIN_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Checkbox checked={reminders.domains.includes(option.value)} />
                              <ListItemText primary={option.label} />
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={reminders.skip_weekends}
                              disabled={subFieldsDisabled}
                              onChange={(event) =>
                                updateReminderSettings({ skip_weekends: event.target.checked })
                              }
                            />
                          )}
                          label="Skip weekends"
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                );
              })()}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Feature Flags
                  </Typography>
                  <Button startIcon={<AddOutlinedIcon />} onClick={addFeatureFlag}>
                    Add Flag
                  </Button>
                </Stack>

                {form.feature_flags.filter((flag) => flag.feature_key !== REMINDER_FLAG_KEY).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No feature flags configured.
                  </Typography>
                ) : null}

                {form.feature_flags
                  .map((flag, index) => ({ flag, index }))
                  .filter(({ flag }) => flag.feature_key !== REMINDER_FLAG_KEY)
                  .map(({ flag, index }) => (
                  <Paper key={`feature-flag-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2">Feature Flag #{index + 1}</Typography>
                        <IconButton color="error" onClick={() => removeFeatureFlag(index)}>
                          <DeleteOutlineOutlinedIcon />
                        </IconButton>
                      </Stack>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={5}>
                          <TextField
                            fullWidth
                            label="Feature Key"
                            value={flag.feature_key}
                            onChange={(event) => updateFeatureFlag(index, 'feature_key', event.target.value)}
                            error={Boolean(getFieldError(fieldErrors, `feature_flags.${index}.feature_key`))}
                            helperText={getFieldError(fieldErrors, `feature_flags.${index}.feature_key`)}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={Boolean(flag.is_enabled)}
                                onChange={(event) => updateFeatureFlag(index, 'is_enabled', event.target.checked)}
                              />
                            )}
                            label="Enabled"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Feature Value JSON"
                            value={flag.feature_value_json}
                            onChange={(event) => updateFeatureFlag(index, 'feature_value_json', event.target.value)}
                            error={Boolean(getFieldError(fieldErrors, `feature_flags.${index}.feature_value`))}
                            helperText={getFieldError(fieldErrors, `feature_flags.${index}.feature_value`) || 'Optional JSON payload.'}
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => navigate(isEditMode ? `${TENANT_BASE_PATH}/${tenantId}` : TENANT_BASE_PATH)}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? 'Saving...' : isEditMode ? 'Update Tenant' : 'Create Tenant'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
